import React from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import AdminAuthModal from '../components/Admin/AdminAuthModal';
import { AdminKeyService } from '../services/adminKeyService';

const AdminLinkPage: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [checking, setChecking] = React.useState(true);
  const [valid, setValid] = React.useState(false);
  const [error, setError] = React.useState('');
  const secret = React.useMemo(() => {
    const pKey = String((params as any)?.key || '').trim();
    if (pKey) return pKey;
    const splat = String((params as any)?.['*'] || '').trim();
    if (splat) return splat;
    const path = String(location?.pathname || '');
    if (path.startsWith('/admin-')) {
      return path.slice('/admin-'.length).trim();
    }
    return '';
  }, [location?.pathname, params]);

  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch {
      // ignore
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setChecking(true);
      setError('');
      setValid(false);
      const toastId = toast.loading('Verifying admin link…');
      try {
        if (!secret) throw new Error('Missing admin link key');
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 12000);

        const res = await fetch('/api/admin-fallback.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'admin_fallback_verify_secret', candidate: secret }),
          signal: controller.signal,
        }).finally(() => window.clearTimeout(timeout));

        const raw = await res.text().catch(() => '');
        const json = (() => {
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        })();
        if (!res.ok || !json?.ok) {
          const details = typeof json?.error === 'string' && json.error.trim() ? json.error.trim() : '';
          throw new Error(details || `Verification failed (${res.status})`);
        }
        if (!json?.valid) {
          throw new Error('Invalid admin link');
        }
        if (!cancelled) {
          AdminKeyService.setLocalSecret(secret);
          setValid(true);
        }
        toast.success('Admin link verified. Please sign in.', { id: toastId });
      } catch (e: any) {
        const msg = e?.name === 'AbortError' ? 'Verification timed out. Please try again.' : (e?.message || 'Invalid admin link');
        if (!cancelled) {
          setError(msg);
        }
        toast.error(msg, { id: toastId });
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [secret]);

  return (
    <Box sx={{ minHeight: '60vh', py: 6 }}>
      <Container maxWidth="sm">
        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
              Admin Access
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {checking ? 'Verifying your admin link…' : valid ? 'Verified. Please sign in or create your admin account.' : 'This admin link is not valid.'}
            </Typography>

            {checking ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Checking the link now. If this takes too long, it will time out and you can retry.
              </Alert>
            ) : valid ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Admin link verified. You can sign in below.
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error || 'Invalid admin link'}
              </Alert>
            )}

            {checking ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !valid ? (
              <>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="contained" onClick={() => navigate('/')} sx={{ textTransform: 'none' }}>
                    Back to Home
                  </Button>
                  <Button variant="outlined" onClick={() => window.location.reload()} sx={{ textTransform: 'none' }}>
                    Retry
                  </Button>
                </Box>
              </>
            ) : (
              <Button variant="outlined" onClick={() => navigate('/')} sx={{ textTransform: 'none' }}>
                Continue
              </Button>
            )}
          </CardContent>
        </Card>
      </Container>

      <AdminAuthModal
        open={valid}
        onClose={() => navigate('/')}
        onSuccess={() => {
          navigate('/admin');
        }}
        prefillAdminSecret={secret}
      />
    </Box>
  );
};

export default AdminLinkPage;
